import { CustomList, CustomSelect, CustomTag, CustomModal, CustomPageHeader } from "../../../ui";
import React, { useContext, useState, useEffect, useCallback } from "react";
import { AuthContext } from "../../../contexts";
import { toastOptions } from "../../../assets/constants";
import axios from "axios";
import { toast } from "react-toastify";
import { PlusIcon, TrashIcon, PencilIcon } from "@phosphor-icons/react";
import dayjs from "dayjs";
import { CircularProgress, Checkbox, FormControlLabel } from "@mui/material";
import { useTokenManagement, useScopeManagement } from "../../../hooks";

const TokensList = () => {
    const { token } = useContext(AuthContext);
    const [selectedColumns, setSelectedColumns] = useState(0);
    const [selectedIds, setSelectedIds] = useState([]);
    const [allTokens, setAllTokens] = useState([]);
    const [refreshCounter, setRefreshCounter] = useState(0);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [availableScopes, setAvailableScopes] = useState([]);

    const [tokenModal, setTokenModal] = useState({
        isOpen: false,
        isCreating: false,
        name: "",
        selectedScopes: [],
        expiresAt: "",
    });

    const [scopeModal, setScopeModal] = useState({
        isOpen: false,
        isCreating: false,
        name: "",
        description: "",
        category: "",
        selectedCategory: null,
        action: "",
    });

    const [editModal, setEditModal] = useState({
        isOpen: false,
        isUpdating: false,
        token: null,
        name: "",
        selectedScopes: [],
        isActive: true,
    });

    const refreshData = useCallback(() => {
        setRefreshCounter((prev) => prev + 1);
    }, []);

    const fetchScopes = useCallback(async () => {
        try {
            const response = await axios.get(process.env.REACT_APP_UPA_API_HOST + "tokens/scopes", {
                headers: { Authorization: "Bearer " + token },
            });
            setAvailableScopes(response.data.scopes || {});
        } catch (error) {
            toast.error("Erreur lors du chargement des scopes", toastOptions);
        }
    }, [token]);

    const { createToken, updateToken, deleteTokens } = useTokenManagement(token, refreshData);
    const { categoryOptions, createScope, generateScopeName } = useScopeManagement(availableScopes, token, fetchScopes);

    const columns = React.useMemo(
        () => [
            {
                Header: "Nom",
                accessor: "name",
                icon: "text-aa",
                Cell: ({ row }) => <span>{row.original.name}</span>,
            },
            {
                Header: "Scopes",
                accessor: "scopes",
                icon: "shield",
                Cell: ({ row }) => (
                    <div className="flex-gap-2">
                        {row.original.scopes.slice(0, 2).map((scope) => (
                            <CustomTag key={scope.id} value={scope.name} />
                        ))}
                        {row.original.scopes.length > 2 && <CustomTag value={`+${row.original.scopes.length - 2} autres`} className="inactive" />}
                    </div>
                ),
            },
            {
                Header: "Statut",
                accessor: "isActive",
                icon: "tag-simple",
                Cell: ({ row }) => {
                    const tokenData = row.original;
                    let className = "active";
                    let text = "Actif";

                    if (!tokenData.isActive) {
                        className = "inactive";
                        text = "Désactivé";
                    } else if (tokenData.expiresAt && dayjs(tokenData.expiresAt).isBefore(dayjs())) {
                        className = "warning";
                        text = "Expiré";
                    }

                    return <CustomTag value={text} className={className} />;
                },
            },
            {
                Header: "Expiration",
                accessor: "expiresAt",
                icon: "calendar",
                Cell: ({ row }) => (row.original.expiresAt ? dayjs(row.original.expiresAt).format("DD/MM/YYYY") : "Jamais"),
            },
            {
                Header: "Dernière utilisation",
                accessor: "lastUsedAt",
                icon: "calendar",
                Cell: ({ row }) => (row.original.lastUsedAt ? dayjs(row.original.lastUsedAt).format("DD/MM/YYYY") : "Jamais"),
            },
            {
                Header: "Créé par",
                accessor: "user",
                icon: "user",
                Cell: ({ row }) => <span>{row.original.user ? row.original.user.name : "Utilisateur supprimé"}</span>,
            },
        ],
        []
    );

    useEffect(() => {
        fetchScopes();
    }, [fetchScopes]);

    const handleBulkDelete = () => {
        setIsDeleteModalOpen(true);
    };

    const confirmBulkDelete = async () => {
        setIsDeleting(true);
        try {
            await deleteTokens(selectedIds);
            setIsDeleteModalOpen(false);
        } catch (err) {
        } finally {
            setIsDeleting(false);
        }
    };

    const handleOpenModal = () => {
        setTokenModal({
            isOpen: true,
            isCreating: false,
            name: "",
            selectedScopes: [],
            expiresAt: "",
        });
    };

    const handleOpenScopeModal = () => {
        setScopeModal({
            isOpen: true,
            isCreating: false,
            name: "",
            description: "",
            category: "",
            selectedCategory: null,
            action: "",
        });
    };

    const handleCategoryChange = (selectedOption) => {
        setScopeModal((prev) => ({
            ...prev,
            selectedCategory: selectedOption,
            category: selectedOption ? selectedOption.value : "",
            name: generateScopeName(selectedOption ? selectedOption.value : prev.category, prev.action),
        }));
    };

    const handleActionChange = (e) => {
        const action = e.target.value;
        setScopeModal((prev) => ({
            ...prev,
            action,
            name: generateScopeName(prev.category, action),
        }));
    };

    const handleCustomCategoryChange = (e) => {
        const category = e.target.value.toLowerCase();
        setScopeModal((prev) => ({
            ...prev,
            category,
            selectedCategory: prev.selectedCategory && prev.selectedCategory.value !== category ? null : prev.selectedCategory,
            name: generateScopeName(category, prev.action),
        }));
    };

    const handleCreateScope = async () => {
        if (!scopeModal.name.trim()) {
            toast.error("Le nom du scope est requis", toastOptions);
            return;
        }
        if (!scopeModal.description.trim()) {
            toast.error("La description du scope est requise", toastOptions);
            return;
        }
        if (!scopeModal.category.trim()) {
            toast.error("La catégorie du scope est requise", toastOptions);
            return;
        }

        setScopeModal((prev) => ({ ...prev, isCreating: true }));
        try {
            await createScope({
                name: scopeModal.name,
                description: scopeModal.description,
                category: scopeModal.category,
            });
            setScopeModal({
                isOpen: false,
                isCreating: false,
                name: "",
                description: "",
                category: "",
                selectedCategory: null,
                action: "",
            });
        } catch (error) {
        } finally {
            setScopeModal((prev) => ({ ...prev, isCreating: false }));
        }
    };

    const handleOpenEditModal = (tokenData) => {
        setEditModal({
            isOpen: true,
            isUpdating: false,
            token: tokenData,
            name: tokenData.name,
            selectedScopes: tokenData.scopes.map((scope) => scope.id),
            isActive: tokenData.isActive,
        });
    };

    const handleEditScopeToggle = (scopeId) => {
        setEditModal((prev) => ({
            ...prev,
            selectedScopes: prev.selectedScopes.includes(scopeId) ? prev.selectedScopes.filter((id) => id !== scopeId) : [...prev.selectedScopes, scopeId],
        }));
    };

    const handleUpdateToken = async () => {
        if (!editModal.name.trim()) {
            toast.error("Le nom du token est requis", toastOptions);
            return;
        }
        if (editModal.selectedScopes.length === 0) {
            toast.error("Sélectionnez au moins un scope", toastOptions);
            return;
        }

        setEditModal((prev) => ({ ...prev, isUpdating: true }));
        try {
            await updateToken(editModal.token.id, {
                name: editModal.name,
                selectedScopes: editModal.selectedScopes,
                isActive: editModal.isActive,
            });
            setEditModal({
                isOpen: false,
                isUpdating: false,
                token: null,
                name: "",
                selectedScopes: [],
                isActive: true,
            });
        } catch (error) {
        } finally {
            setEditModal((prev) => ({ ...prev, isUpdating: false }));
        }
    };

    const handleScopeToggle = (scopeId) => {
        setTokenModal((prev) => ({
            ...prev,
            selectedScopes: prev.selectedScopes.includes(scopeId) ? prev.selectedScopes.filter((id) => id !== scopeId) : [...prev.selectedScopes, scopeId],
        }));
    };

    const handleCreateToken = async () => {
        if (!tokenModal.name.trim()) {
            toast.error("Le nom du token est requis", toastOptions);
            return;
        }
        if (tokenModal.selectedScopes.length === 0) {
            toast.error("Sélectionnez au moins un scope", toastOptions);
            return;
        }

        setTokenModal((prev) => ({ ...prev, isCreating: true }));
        try {
            await createToken({
                name: tokenModal.name,
                selectedScopes: tokenModal.selectedScopes,
                expiresAt: tokenModal.expiresAt,
            });
            setTokenModal({
                isOpen: false,
                isCreating: false,
                name: "",
                selectedScopes: [],
                expiresAt: "",
            });
        } catch (error) {
        } finally {
            setTokenModal((prev) => ({ ...prev, isCreating: false }));
        }
    };

    return (
        <div className="c-app__view">
            <CustomPageHeader 
                title="Tokens API"
                buttons={selectedColumns === 0 ? [
                    {
                        label: "Nouveau scope",
                        onClick: handleOpenScopeModal,
                        icon: PlusIcon,
                        variant: "secondary"
                    },
                    {
                        label: "Nouveau token",
                        onClick: handleOpenModal,
                        icon: PlusIcon,
                        variant: "primary"
                    }
                ] : [
                    ...(selectedIds.length === 1 ? [
                        {
                            label: "Modifier",
                            onClick: () => {
                                const selectedToken = allTokens.find((token) => token.id === selectedIds[0]);
                                if (selectedToken) handleOpenEditModal(selectedToken);
                            },
                            icon: PencilIcon,
                            variant: "secondary"
                        }
                    ] : []),
                    {
                        label: "Supprimer",
                        onClick: handleBulkDelete,
                        icon: TrashIcon,
                        variant: "danger"
                    }
                ]}
            />
            <div className="tabs-container">
                <CustomList
                    entity="tokens/all"
                    isClickable={false}
                    refreshData={refreshCounter}
                    columns={columns}
                    setSelectedColumns={setSelectedColumns}
                    setSelectedIds={setSelectedIds}
                    setData={setAllTokens}
                />
            </div>

            <CustomModal show={isDeleteModalOpen} setShow={setIsDeleteModalOpen} title="Confirmer la suppression">
                <div className="c-modal-content is-center">
                    <p>
                        Êtes-vous sûr de vouloir supprimer{" "}
                        <strong>
                            {allTokens
                                .filter((token) => selectedIds.includes(token.id))
                                .map((token) => token.name)
                                .join(", ")}
                        </strong>{" "}
                        ?
                    </p>
                    <p>Cette action est irréversible.</p>
                </div>
                <div className="modal-buttons">
                    <button className="button-large cancel-button" onClick={() => setIsDeleteModalOpen(false)} disabled={isDeleting}>
                        Annuler
                    </button>
                    <button className="button-large red-button" onClick={confirmBulkDelete} disabled={isDeleting}>
                        <TrashIcon size={16} weight="bold" />
                        {isDeleting ? <CircularProgress size={16} color="inherit" /> : "Supprimer"}
                    </button>
                </div>
            </CustomModal>

            <CustomModal show={tokenModal.isOpen} setShow={(show) => setTokenModal((prev) => ({ ...prev, isOpen: show }))} title="Nouveau token API">
                <div className="custom__modal-group">
                    <label className="name">Nom du token</label>
                    <input
                        type="text"
                        placeholder="Écrivez le nom du token..."
                        value={tokenModal.name}
                        onChange={(e) => setTokenModal((prev) => ({ ...prev, name: e.target.value.toUpperCase() }))}
                        className="classic-input"
                    />
                </div>

                <div className="custom__modal-group">
                    <label className="date">Date d'expiration (optionnel)</label>
                    <input type="date" value={tokenModal.expiresAt} onChange={(e) => setTokenModal((prev) => ({ ...prev, expiresAt: e.target.value }))} className="classic-input" />
                    <small>Laissez vide pour un token sans expiration</small>
                </div>

                <div className="custom__modal-group">
                    <label className="password">Permissions (scopes)</label>
                    <div className="custom__modal-checkbox">
                        {Object.keys(availableScopes).length > 0 ? (
                            Object.entries(availableScopes).map(([category, scopes]) => (
                                <div className="custom__modal-token" key={category}>
                                    <p>{category}</p>
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                                        {scopes.map((scope) => (
                                            <FormControlLabel
                                                key={scope.id}
                                                control={<Checkbox checked={tokenModal.selectedScopes.includes(scope.id)} onChange={() => handleScopeToggle(scope.id)} size="small" />}
                                                label={scope.name}
                                                style={{ margin: 0 }}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="loader-container absolute">
                                <CircularProgress size={30} />
                            </div>
                        )}
                    </div>
                </div>

                <div className="modal-buttons">
                    <button onClick={() => setTokenModal((prev) => ({ ...prev, isOpen: false }))} className="button-large cancel-button" disabled={tokenModal.isCreating}>
                        Annuler
                    </button>
                    <button onClick={handleCreateToken} className="button-large green-button" disabled={tokenModal.isCreating || !tokenModal.name.trim() || tokenModal.selectedScopes.length === 0}>
                        {tokenModal.isCreating ? <CircularProgress size={16} className="modal-progress" /> : <PlusIcon size={16} />}
                        <span>{tokenModal.isCreating ? "Création..." : "Créer le token"}</span>
                    </button>
                </div>
            </CustomModal>

            <CustomModal show={scopeModal.isOpen} setShow={(show) => setScopeModal((prev) => ({ ...prev, isOpen: show }))} title="Nouveau scope API">
                <div className="custom__modal-group">
                    <label className="tag-simple">Catégorie</label>
                    <CustomSelect
                        options={categoryOptions}
                        value={scopeModal.selectedCategory}
                        onChange={handleCategoryChange}
                        placeholder={categoryOptions.length > 0 ? `${categoryOptions.length} catégories disponibles` : "Aucune catégorie existante - créez-en une ci-dessous"}
                        isClearable
                        isSearchable
                        isDisabled={categoryOptions.length === 0}
                        noOptionsMessage={() => "Aucune catégorie trouvée"}
                    />
                    {categoryOptions.length > 0 && <small>{scopeModal.selectedCategory ? "Catégorie sélectionnée" : "Ou créez une nouvelle catégorie ci-dessous"}</small>}
                </div>

                {!scopeModal.selectedCategory && (
                    <div className="custom__modal-group">
                        <label className="tag-simple">Nouvelle catégorie</label>
                        <input type="text" placeholder="Exemple : livie, monitor, meta, admin..." value={scopeModal.category} onChange={handleCustomCategoryChange} className="classic-input" />
                    </div>
                )}

                <div className="custom__modal-group">
                    <label className="name">Action du scope</label>
                    <input type="text" placeholder="Exemple : read, write, full, admin..." value={scopeModal.action} onChange={handleActionChange} className="classic-input" />
                    <small>
                        Le nom final sera : {scopeModal.category || "catégorie"}:{scopeModal.action || "action"}
                    </small>
                </div>

                <div className="custom__modal-group">
                    <label className="name">Nom du scope (généré automatiquement)</label>
                    <input
                        type="text"
                        value={scopeModal.name}
                        onChange={(e) => setScopeModal((prev) => ({ ...prev, name: e.target.value.toLowerCase() }))}
                        className="classic-input"
                        placeholder="Le nom sera généré automatiquement..."
                    />
                </div>

                <div className="custom__modal-group">
                    <label className="chat">Description</label>
                    <input
                        type="text"
                        placeholder="Description du scope..."
                        value={scopeModal.description}
                        onChange={(e) => setScopeModal((prev) => ({ ...prev, description: e.target.value }))}
                        className="classic-input"
                    />
                </div>

                <div className="modal-buttons">
                    <button onClick={() => setScopeModal((prev) => ({ ...prev, isOpen: false }))} className="button-large cancel-button" disabled={scopeModal.isCreating}>
                        Annuler
                    </button>
                    <button
                        onClick={handleCreateScope}
                        className="button-large green-button"
                        disabled={scopeModal.isCreating || !scopeModal.name.trim() || !scopeModal.description.trim() || !scopeModal.category.trim()}
                    >
                        {scopeModal.isCreating ? <CircularProgress size={16} className="modal-progress" /> : <PlusIcon size={16} />}
                        <span>{scopeModal.isCreating ? "Création..." : "Créer le scope"}</span>
                    </button>
                </div>
            </CustomModal>

            <CustomModal show={editModal.isOpen} setShow={(show) => setEditModal((prev) => ({ ...prev, isOpen: show }))} title="Modifier le token">
                <div className="custom__modal-group">
                    <label className="name">Nom du token</label>
                    <input
                        type="text"
                        placeholder="Nom du token..."
                        value={editModal.name}
                        onChange={(e) => setEditModal((prev) => ({ ...prev, name: e.target.value.toUpperCase() }))}
                        className="classic-input"
                    />
                </div>

                <div className="custom__modal-group">
                    <label className="tag-simple">Statut</label>
                    <div className="flex-gap-2" style={{ alignItems: "center" }}>
                        <FormControlLabel
                            control={<Checkbox checked={editModal.isActive} onChange={(e) => setEditModal((prev) => ({ ...prev, isActive: e.target.checked }))} size="small" />}
                            label="Token actif"
                            style={{ margin: 0 }}
                        />
                    </div>
                </div>

                <div className="custom__modal-group">
                    <label className="password">Permissions (scopes)</label>
                    <div className="custom__modal-checkbox">
                        {Object.keys(availableScopes).length > 0 ? (
                            Object.entries(availableScopes).map(([category, scopes]) => (
                                <div className="custom__modal-token" key={category}>
                                    <p>{category}</p>
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                                        {scopes.map((scope) => (
                                            <FormControlLabel
                                                key={scope.id}
                                                control={<Checkbox checked={editModal.selectedScopes.includes(scope.id)} onChange={() => handleEditScopeToggle(scope.id)} size="small" />}
                                                label={scope.name}
                                                style={{ margin: 0 }}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="loader-container absolute">
                                <CircularProgress size={30} />
                            </div>
                        )}
                    </div>
                </div>

                <div className="modal-buttons">
                    <button onClick={() => setEditModal((prev) => ({ ...prev, isOpen: false }))} className="button-large cancel-button" disabled={editModal.isUpdating}>
                        Annuler
                    </button>
                    <button onClick={handleUpdateToken} className="button-large green-button" disabled={editModal.isUpdating || !editModal.name.trim() || editModal.selectedScopes.length === 0}>
                        {editModal.isUpdating ? <CircularProgress size={16} className="modal-progress" /> : <PencilIcon size={16} />}
                        <span>{editModal.isUpdating ? "Modification..." : "Modifier le token"}</span>
                    </button>
                </div>
            </CustomModal>
        </div>
    );
};

export default TokensList;
